import React, { useState, useEffect, useMemo } from "react";
import { CheckboxListFilter } from "../../../checkbox-list-filter/CheckboxListFilter";
import { useXemelgoClient } from "../../../../services/xemelgo-service";
import { DatePicker } from "../../../DateTimePicker/DateTimePicker";
import SearchDropdown from "../../../SearchDropdown/SearchDropdown";
import { applySideFilters } from "../../../../features/side-filter/services/side-filter-configuration-provider/sideFilterConfigurationProvider";
import { naturalSort } from "../../../../common/Utilities";
import { TEMPLATE_OPTIONS, FILTER_TYPE } from "../../data/constants";
import TagInput from "../../../tag-input";
import xemelgoStyle from "../../../../styles/variable";

export const useFiltersConfigBuilder = (filterConfig) => {
  const [initialFilterValue, setInitialFilterValue] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const xemelgoClient = useXemelgoClient();

  const getDateRangeConfigStructure = (eachFilter) => {
    return {
      id: eachFilter.id,
      title: eachFilter.title,
      filters: [
        {
          id: "fromDateTime",
          title: "From:",
          renderComponent: (sideFilterValues, updateFilterValues) => {
            return (
              <DatePicker
                color={xemelgoStyle.theme.APP_BLUE}
                maxDate={sideFilterValues[eachFilter.id]?.endTime}
                placeholder="Select start date"
                value={sideFilterValues[eachFilter.id]?.startTime}
                onTimeChange={(timestamp) => {
                  updateFilterValues({ [eachFilter.id]: { ...sideFilterValues[eachFilter.id], startTime: timestamp } });
                }}
              />
            );
          },
          filterToApply: (sideFilterValues, eachItem) => {
            return (
              !sideFilterValues[eachFilter.id] ||
              !sideFilterValues[eachFilter.id].startTime ||
              (eachItem[eachFilter.id] && sideFilterValues[eachFilter.id].startTime <= eachItem[eachFilter.id])
            );
          }
        },
        {
          id: "toDateTime",
          title: "To:",
          renderComponent: (sideFilterValues, updateFilterValues) => {
            return (
              <DatePicker
                color={xemelgoStyle.theme.APP_BLUE}
                minDate={sideFilterValues[eachFilter.id]?.startTime}
                placeholder="Select end date"
                value={sideFilterValues[eachFilter.id]?.endTime}
                onTimeChange={(timestamp) => {
                  updateFilterValues({ [eachFilter.id]: { ...sideFilterValues[eachFilter.id], endTime: timestamp } });
                }}
              />
            );
          },
          filterToApply: (sideFilterValues, eachItem) => {
            return (
              !sideFilterValues[eachFilter.id] ||
              !sideFilterValues[eachFilter.id].endTime ||
              (eachItem[eachFilter.id] && sideFilterValues[eachFilter.id]?.endTime >= eachItem[eachFilter.id])
            );
          }
        }
      ]
    };
  };

  const getCheckboxesConfigStructure = (eachFilter) => {
    return {
      id: eachFilter.id,
      title: eachFilter.title,
      filters: [
        {
          renderComponent: (sideFilterValues, updateFilterValues) => {
            return (
              <CheckboxListFilter
                onChange={(newCheckboxList) => {
                  updateFilterValues({ [eachFilter.id]: [...newCheckboxList] });
                }}
                checkboxList={sideFilterValues[eachFilter.id]}
              />
            );
          },
          filterToApply: (sideFilterValues, eachItem) => {
            switch (eachFilter.id) {
              case TEMPLATE_OPTIONS.status:
                const selectedStatusList = (sideFilterValues[eachFilter.id] || []).filter((eachCheckbox) => {
                  return eachCheckbox.value;
                });
                let hasStatus = !selectedStatusList.length || false;
                eachItem[eachFilter.id].forEach((eachStatus) => {
                  const { status, severity } = eachStatus;
                  selectedStatusList.forEach((eachSelectedStatus) => {
                    const { status: selectedStatus, severity: selectedSeverity } = eachSelectedStatus;
                    if (status === selectedStatus && severity === selectedSeverity) {
                      hasStatus = true;
                    }
                  });
                });
                return hasStatus;
              default:
                const selectedList = [];
                (sideFilterValues[eachFilter.id] || []).forEach((eachCheckbox) => {
                  if (eachCheckbox.value) {
                    selectedList.push(eachCheckbox.id?.toLowerCase?.());
                  }
                });
                return !selectedList.length || selectedList.includes(eachItem[eachFilter.id]?.toLowerCase?.());
            }
          }
        }
      ]
    };
  };

  const getSearchDropdownConfigStructure = (eachFilter) => {
    return {
      id: eachFilter.id,
      title: eachFilter.title,
      filters: [
        {
          renderComponent: (sideFilterValues, updateFilterValues) => {
            return (
              <SearchDropdown
                placeholder={`Select a ${eachFilter.title.toLowerCase() || "option"}`}
                options={sideFilterValues[eachFilter.id]?.options || []}
                onItemSelected={(newSelectedItem) => {
                  updateFilterValues({
                    [eachFilter.id]: { value: newSelectedItem, options: sideFilterValues[eachFilter.id]?.options || [] }
                  });
                }}
                selectedItem={sideFilterValues[eachFilter.id]?.value || {}}
                showIcon
              />
            );
          },
          filterToApply: (sideFilterValues, eachItem) => {
            return (
              sideFilterValues[eachFilter.id]?.value === null ||
              !sideFilterValues[eachFilter.id]?.value?.id ||
              sideFilterValues[eachFilter.id].value.id === eachItem[eachFilter.id]
            );
          }
        }
      ]
    };
  };

  const getInputConfigStructure = (eachFilter) => {
    return {
      id: eachFilter.id,
      title: eachFilter.title,
      filters: [
        {
          renderComponent: (sideFilterValues, updateFilterValues) => {
            return (
              <TagInput
                values={sideFilterValues[eachFilter.id]?.value}
                onChange={(newValue) => {
                  updateFilterValues({ [eachFilter.id]: { value: newValue } });
                }}
              />
            );
          },
          filterToApply: (sideFilterValues, eachItem) => {
            return (
              !sideFilterValues[eachFilter.id]?.value?.length ||
              sideFilterValues[eachFilter.id].value
                .map((eachValue) => {
                  return eachValue.toLowerCase();
                })
                .includes(eachItem[eachFilter.id].toLowerCase())
            );
          }
        }
      ]
    };
  };

  const getFilterStructureBuilderByType = (type) => {
    switch (type) {
      case FILTER_TYPE.checkboxes:
        return getCheckboxesConfigStructure;
      case FILTER_TYPE.dateRange:
        return getDateRangeConfigStructure;
      case FILTER_TYPE.searchDropdown:
        return getSearchDropdownConfigStructure;
      case FILTER_TYPE.input:
        return getInputConfigStructure;
      default:
        return () => {};
    }
  };

  const filterConfiguration = useMemo(() => {
    const config = { categories: [] };
    filterConfig.forEach((eachFilter) => {
      const filterStructureBuilder = getFilterStructureBuilderByType(eachFilter.type);
      const newCategory = filterStructureBuilder(eachFilter);
      if (newCategory) {
        config.categories.push(newCategory);
      }
    });
    return config;
  }, [filterConfig]);

  const getTemplateOptionsByType = (optionId, eachFilterConfig) => {
    switch (optionId) {
      case TEMPLATE_OPTIONS.location:
        return fetchLocation(eachFilterConfig.locationCategories);
      case TEMPLATE_OPTIONS.itemType:
        return fetchItemType();
      default:
        return [];
    }
  };

  const fetchLocation = async (categories) => {
    const locationClient = xemelgoClient.getLocationClient();
    const locationTreeMap = await locationClient.getLocationTree([], categories);
    return naturalSort(
      Object.values(locationTreeMap).map((eachLocation) => {
        return { id: eachLocation.id, label: eachLocation.name };
      }),
      "label"
    );
  };

  const fetchItemType = async () => {
    const itemTypeClient = xemelgoClient.getItemTypeClient();
    const itemTypes = await itemTypeClient.getItemTypes(["id", "identifier"]);
    return itemTypes.map((eachItemType) => {
      return { id: eachItemType.identifier, label: eachItemType.identifier };
    });
  };

  const updateInitialFilterValue = async () => {
    setIsLoading(true);
    const initialFilterValueObj = {};
    await Promise.all(
      filterConfig.map(async (eachFilter) => {
        switch (eachFilter.type) {
          case FILTER_TYPE.checkboxes:
            initialFilterValueObj[eachFilter.id] = (
              eachFilter.optionTemplate
                ? await getTemplateOptionsByType(eachFilter.optionTemplate, eachFilter)
                : [...(eachFilter.options || [])]
            ).map((eachValue) => {
              return { ...eachValue, value: false };
            });
            break;
          case FILTER_TYPE.searchDropdown:
            initialFilterValueObj[eachFilter.id] = {
              options: eachFilter.optionTemplate
                ? await getTemplateOptionsByType(eachFilter.optionTemplate, eachFilter)
                : [...(eachFilter.options || [])],
              value: eachFilter.defaultValue || null
            };
            break;
          case FILTER_TYPE.dateRange:
            initialFilterValueObj[eachFilter.id] = { startTime: null, endTime: null };
            break;
          case FILTER_TYPE.input:
            initialFilterValueObj[eachFilter.id] = { value: [] };
            break;
          default:
            break;
        }
      })
    );
    setInitialFilterValue(initialFilterValueObj);
    setIsLoading(false);
  };

  useEffect(() => {
    updateInitialFilterValue();
  }, [filterConfig]);

  return { filterConfiguration, initialFilterValue, applySideFilters, isLoading };
};
